import { Toaster } from "sonner";
import { Routes } from "./routes/Routes";
import Theme from "./Theme";
import { GlobalStyles } from "./GlobalStyles.styles";
import { useEffect, useState } from "react";
import axios, { CanceledError } from "axios";
import { SecurityScreen } from "@src/components/SecurityScreen/SecurityScreen";
import { MsalProvider } from "@azure/msal-react";
import { AccountInfo, PublicClientApplication } from "@azure/msal-browser";
import { msalConfig, tokenRequest } from "./utils/msalConfig";
import { getTokenUserName, isTokenExpired } from "@src/utils/accessToken";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import {CellSelectionModule, ClipboardModule, LicenseManager} from 'ag-grid-enterprise';
import "@src/utils/googleAnalytics";

LicenseManager.setLicenseKey(
	'I_<3_SCHOOL_NDEwMjMzMzIwMDAwMA==afc05c982fa05a2578eb9cab60c42d78',
)
ModuleRegistry.registerModules([
	ClipboardModule,
	CellSelectionModule,
	AllCommunityModule,
]);
// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Ensure MSAL is properly initialized before rendering
//@ts-expect-error - MSAL instance is not yet typed
await msalInstance.initialize();

const isTopWindow = window.self === window.top;

const isAccountOf = (userName: string) => (account: AccountInfo) =>
	account.username.toLowerCase() === userName.toLowerCase();

const getTabAccount = (): AccountInfo | null => {
	const userName = sessionStorage.getItem("userName");
	if (!userName) {
		return null;
	}
	return msalInstance.getAllAccounts().find(isAccountOf(userName)) ?? null;
};

const endSession = () => {
	sessionStorage.removeItem("token");
	window.location.reload();
};

const resumeMsalSession = async (userName: string) => {
	if (getTabAccount()) {
		return;
	}
	try {
		await msalInstance.ssoSilent({ ...tokenRequest, loginHint: userName });
	} catch (error) {
		console.error("Silent sign-in failed:", error);
	}
};

let pendingSessionResume: Promise<void> = Promise.resolve();

const acquireMsalToken = async (): Promise<string | null> => {
	const account = getTabAccount();
	if (!account) {
		return null;
	}
	try {
		const { accessToken } = await msalInstance.acquireTokenSilent({ ...tokenRequest, account });
		sessionStorage.setItem("token", accessToken);
		return accessToken;
	} catch (error) {
		console.error("Silent token refresh failed:", error);
		return null;
	}
};

const getRequestToken = async (): Promise<string | null> => {
	const msalToken = await acquireMsalToken();
	if (msalToken) {
		return msalToken;
	}
	const storedToken = sessionStorage.getItem("token");
	if (storedToken && !isTokenExpired(storedToken)) {
		return storedToken;
	}
	await pendingSessionResume;
	return acquireMsalToken();
};

const AppContent = () => {
	const [isInitialized, setIsInitialized] = useState(false);
	const [userName, setUserName] = useState("");

	useEffect(() => {
		// Check for token in URL params
		const urlParams = new URLSearchParams(window.location.search);
		const urlToken = urlParams.get("token");

		// If token exists in URL, store it and clean the URL
		if (urlToken) {
			sessionStorage.setItem("token", urlToken);
			// Remove token from URL to avoid exposing it
			window.history.replaceState({}, document.title, window.location.pathname);
		}

		// Check for token in session storage
		const token = sessionStorage.getItem("token");

		const tokenUserName = token ? getTokenUserName(token) : "";
		if (tokenUserName) {
			setUserName(tokenUserName);
			sessionStorage.setItem("userName", tokenUserName);
			pendingSessionResume = resumeMsalSession(tokenUserName);
		} else {
			sessionStorage.removeItem("userName");
		}

		const requestInterceptor = axios.interceptors.request.use(async config => {
			const requestToken = await getRequestToken();
			if (!requestToken) {
				endSession();
				throw new CanceledError("Session expired");
			}
			config.headers.Authorization = `Bearer ${requestToken}`;
			return config;
		});

		const interceptor = axios.interceptors.response.use(
			response => response,
			error => {
				if (error.response && error.response.status === 401) {
					endSession();
				}
				return Promise.reject(error);
			},
		);

		setIsInitialized(true);

		return () => {
			axios.interceptors.request.eject(requestInterceptor);
			axios.interceptors.response.eject(interceptor);
		};
	}, []);

	if (!isInitialized) {
		return null; // or a loading spinner
	}

	const token = sessionStorage.getItem("token");
	if (!token) {
		return <SecurityScreen />;
	}

	return (
		<Theme>
			<GlobalStyles />
			<Routes />
			<Toaster duration={2000} position="top-center" expand={false} closeButton />
			<div id="modal"></div>
		</Theme>
	);
};

const App = () => {
	if (!isTopWindow) {
		return null;
	}

	return (
		<MsalProvider instance={msalInstance}>
			<AppContent />
		</MsalProvider>
	);
};

export default App;